import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as turf from '@turf/turf';

import CursorLatLng from '../map/CursorLatLng';
import MapContainer from '../map/MapContainer';
import useInitializeMapEffect from '../../hooks/useInitializeMapEffect';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import { addImageToMap } from '../../utils/map';
import { getRootUrl } from '../../utils/utils';
import { emptyFeature, emptyGeojson } from '../../utils/geojson';
import { MAP_ACTIVE_COLOR } from '../../utils/colors';
import { ItemContext } from '../../contexts/ItemContext';
import DashboardMapInfoCard from './DashboardMapInfoCard';
import { CONUS_CENTER } from '../../constants/coords';
import fitMapBounds from '../../utils/fitMapBounds';

const INIT_ZOOM = 3;
const ANIMATION_OPTIONS = { duration: 1000 };

const ACTIVE_POINTS_SOURCE = 'ACTIVE_POINTS_SOURCE';
const ACTIVE_POINTS_LAYER = 'ACTIVE_POINTS_LAYER';

const FARM_POINTS_SOURCE = 'FARM_POINTS_SOURCE';
const FARM_POINTS_LAYER = 'FARM_POINTS_LAYER';

const pointLayerSpec = {
  type: 'symbol',
  layout: {
    'icon-image': 'pin',
    'icon-size': 0.67,
    'icon-allow-overlap': true, // this is required to avoid fade-in/fade-out when active farm changes
  },
};

const onMapLoad = (mapRef) => {
  // ADD IMAGES
  const rootUrl = getRootUrl();
  addImageToMap(mapRef.current, 'pin', `${rootUrl}/images/Pin.png`, {
    sdf: true,
  });

  // ADD SOURCES
  mapRef.current.addSource(ACTIVE_POINTS_SOURCE, emptyGeojson());
  mapRef.current.addSource(FARM_POINTS_SOURCE, emptyGeojson());

  // ADD LAYERS
  mapRef.current.addLayer({
    id: FARM_POINTS_LAYER,
    source: FARM_POINTS_SOURCE,
    ...pointLayerSpec,
    paint: {
      'icon-color': ['get', 'color'],
    },
  });
  mapRef.current.addLayer({
    id: ACTIVE_POINTS_LAYER,
    source: ACTIVE_POINTS_SOURCE,
    ...pointLayerSpec,
    paint: {
      'icon-color': MAP_ACTIVE_COLOR,
    },
  });
};

/**
 * Show farm points on map.
 */
const useFarmPointsLayer = ({ mapRef, mapLoaded }) => {
  const {
    dashboards: { dashboardFarms },
  } = useMultiProjectDashboardContext();

  useEffect(() => {
    if (!mapLoaded) return;

    const source = mapRef.current.getSource(FARM_POINTS_SOURCE);
    if (!source) return;

    source.setData(
      turf.featureCollection(
        dashboardFarms.map((farm) => ({
          ...farm.mapCenter,
          properties: {
            ...farm.mapCenter.properties,
            farmId: farm.id,
            color: farm.color,
          },
        })),
      ),
    );
  }, [mapRef, dashboardFarms, mapLoaded]);
};

/**
 * Highlight farms on map when hovered in the left panel.
 */
const useActivePointsLayer = ({ mapRef, mapLoaded }) => {
  const {
    dashboards: { dashboardFarms },
  } = useMultiProjectDashboardContext();
  const { activeKey: hoveredFarmId } = useContext(ItemContext);

  // Set/update layer data
  useEffect(() => {
    if (!mapLoaded) return;

    const source = mapRef.current.getSource(ACTIVE_POINTS_SOURCE);
    if (!source) return;

    const activeFarmFeature = dashboardFarms.find(
      ({ id }) => id === hoveredFarmId,
    )?.mapCenter;

    source.setData(activeFarmFeature ?? emptyFeature());
  }, [mapRef, dashboardFarms, hoveredFarmId, mapLoaded]);
};

/**
 * Fit map to bounds of all farms when farms change.
 *
 * Or, if 1 farm is selected, center that farm (i.e. if `lngLat` is set).
 */
const useFitMapToPoints = ({ mapRef, mapLoaded, lngLat }) => {
  const {
    dashboards: { dashboardFarms },
  } = useMultiProjectDashboardContext();

  const farmCenters = useMemo(() => {
    if (!mapLoaded) return [];
    return dashboardFarms.map(({ mapCenter }) => mapCenter);
  }, [dashboardFarms, mapLoaded]);

  // Fit map to bounds when bounds change
  useEffect(() => {
    if (lngLat && mapRef.current) {
      // if 1 farm is selected, center that farm
      const [lng, lat] = lngLat;
      mapRef.current.flyTo({
        center: { lng, lat },
        ...ANIMATION_OPTIONS,
      });
    } else {
      // otherwise, show all farms:
      fitMapBounds({ mapRef, features: farmCenters });
    }
  }, [mapRef, farmCenters, lngLat]);
};

/**
 * Set up hover & click interactions for farm points.
 */
const useFarmPointsInteractions = ({ mapRef }) => {
  const { clearActiveKey, setActiveKey } = useContext(ItemContext);
  const {
    dashboards: { toggleFarm },
  } = useMultiProjectDashboardContext();

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return () => {};

    const getFarmId = (e) => {
      const [feature] = map.queryRenderedFeatures(e.point, {
        layers: [FARM_POINTS_LAYER],
      });
      const { farmId } = feature?.properties ?? {};
      return farmId;
    };

    const onClick = (e) => {
      const farmId = getFarmId(e);
      if (farmId) {
        toggleFarm(farmId);
      }
    };

    const onMouseMove = (e) => {
      const farmId = getFarmId(e);
      if (farmId) {
        setActiveKey(farmId);
      } else {
        clearActiveKey();
      }
    };

    map.on('mousemove', FARM_POINTS_LAYER, onMouseMove);
    map.on('mouseleave', FARM_POINTS_LAYER, clearActiveKey);
    map.on('click', FARM_POINTS_LAYER, onClick);

    return () => {
      if (!map || map._removed) return; // eslint-disable-line no-underscore-dangle

      map.off('mousemove', FARM_POINTS_LAYER, onMouseMove);
      map.off('mouseleave', FARM_POINTS_LAYER, clearActiveKey);
      map.off('click', FARM_POINTS_LAYER, onClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);
};

const DashboardMap = () => {
  const {
    dashboards: { selectedFarmId, dashboardFarmsMap, selectFarm },
    resetState,
  } = useMultiProjectDashboardContext();

  // Reset state on unmount:
  useEffect(() => {
    return () => {
      resetState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetState is stable
  }, []);

  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const farmData = selectedFarmId && dashboardFarmsMap[selectedFarmId];
  const lngLat = farmData?.mapCenter?.geometry?.coordinates;

  useInitializeMapEffect({
    mapContainer,
    mapRef,
    mapLoaded,
    setMapLoaded,
    onLoad: onMapLoad,
    ...CONUS_CENTER,
    zoom: INIT_ZOOM,
  });

  useFarmPointsLayer({ mapRef, mapLoaded });
  useActivePointsLayer({ mapRef, mapLoaded });
  useFitMapToPoints({ mapRef, mapLoaded, lngLat });
  useFarmPointsInteractions({ mapRef });

  return (
    <>
      <MapContainer ref={mapContainer} className="map-container h-100 w-100" />

      {lngLat && farmData && (
        <DashboardMapInfoCard
          mapRef={mapRef}
          lngLat={lngLat}
          farmData={farmData}
          onClose={() => selectFarm(null)}
        />
      )}
      <CursorLatLng mapRef={mapRef} mapLoaded={mapLoaded} />
    </>
  );
};

export default DashboardMap;
